import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Typography,
  Box,
  Snackbar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  AppBar,
  Toolbar,
  Grid,
  TableContainer,
  Button,
  Menu,
  MenuItem,
  TextField,
  TableFooter,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  ListSubheader,
  Chip,
  FormControlLabel,
  Switch,
  TableSortLabel,
  Popover,
  Divider,
} from '@material-ui/core';
import axios from 'axios';
import envConfig from '../../bin/env.config';
import { useSelector } from 'react-redux';
import { SaveAlt as SaveAltIcon } from '@material-ui/icons';
import CircularProgress from '@material-ui/core/CircularProgress';
import BarChartIcon from '@material-ui/icons/BarChart';
import TuneIcon from '@material-ui/icons/Tune';
import ChartsDialog from './CustomerReport/ChartsOutOfDateDialog';
import ProductStatusByCityChart from './CustomerReport/ProductStatusByCityChart';
import { Close as CloseIcon } from '@material-ui/icons';
import CitySelector from './CustomerReport/CitySelector';
import BannerSelector from './CustomerReport/BannerSelector';
import PmgCompanySummaryCell from './CustomerReport/PmgCompanySummaryCell';
import OodCellDetailDialog from './CustomerReport/OodCellDetailDialog';
import {
  METRIC_GROUPS,
  DEFAULT_METRIC,
  METRIC_PRESETS,
  DATE_PERIOD_OPTIONS,
  findMetricOption,
  getMetricTheme,
  getMetricDisplayValue,
  getMetricFooterValue,
  computeColumnMax,
  pivotPmgCompanyData,
  buildStoreDrilldownUrl,
  buildAllMetricsCsv,
  isProblemRow,
  sortRowsByColumn,
  collectMetricValues,
  loadSavedMetric,
  saveMetricPreference,
} from './CustomerReport/oodMetricConfig';

const ROW_HEIGHT = 48;
const VIRTUAL_BUFFER = 8;

function OutOfDateTableRow({
  row,
  columns,
  selectedMetric,
  columnMaxMap,
  heatmapMode,
  heatmapValueMap,
  onDrilldown,
  onDetail,
  style,
}) {
  return (
    <TableRow key={row.pmg_id} style={style}>
      <TableCell
        style={{ backgroundColor: '#f5f5f5', width: '100px', cursor: 'pointer' }}
        onClick={() => window.open(`/matching-group/${row.pmg_id}`, '_blank')}
      >
        {row.pmg_id}
      </TableCell>
      <TableCell
        style={{ backgroundColor: '#f5f5f5', maxWidth: '400px', cursor: 'pointer' }}
        onClick={() => window.open(`/matching-group/${row.pmg_id}`, '_blank')}
      >
        <span>{row.name}</span>
      </TableCell>
      {columns.map((col) => (
        <PmgCompanySummaryCell
          key={col.company_id}
          stats={row.cells[col.company_id]}
          metricKey={selectedMetric}
          columnMax={columnMaxMap[col.company_id]}
          heatmapMode={heatmapMode}
          heatmapValues={heatmapValueMap[col.company_id] || []}
          onDrilldown={onDrilldown}
          onDetail={onDetail}
        />
      ))}
    </TableRow>
  );
}

function OutOfDateTable({
  data,
  selectedMetric,
  problemFilter,
  problemThreshold,
  heatmapMode,
  sortColumnId,
  sortDirection,
  onSortColumn,
  pinnedCompanyIds,
  onDrilldown,
  onDetail,
  showDelta,
}) {
  const { rows, columns } = useMemo(() => pivotPmgCompanyData(data), [data]);
  const containerRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(600);

  const activeMetric = findMetricOption(selectedMetric);
  const metricTheme = getMetricTheme(selectedMetric);

  const displayColumns = useMemo(() => {
    const pinned = columns.filter((c) => pinnedCompanyIds.includes(c.company_id));
    const rest = columns.filter((c) => !pinnedCompanyIds.includes(c.company_id));
    return [...pinned, ...rest];
  }, [columns, pinnedCompanyIds]);

  const filteredRows = useMemo(() => {
    let result = rows;
    if (problemFilter) {
      result = result.filter((row) => isProblemRow(row, problemThreshold, 1));
    }
    if (sortColumnId) {
      result = sortRowsByColumn(result, sortColumnId, selectedMetric, sortDirection);
    }
    return result;
  }, [rows, problemFilter, problemThreshold, sortColumnId, sortDirection, selectedMetric]);

  const columnMaxMap = useMemo(() => {
    const map = {};
    displayColumns.forEach((col) => {
      map[col.company_id] = computeColumnMax(filteredRows, col.company_id);
    });
    return map;
  }, [filteredRows, displayColumns]);

  const heatmapValueMap = useMemo(() => {
    const map = {};
    displayColumns.forEach((col) => {
      map[col.company_id] = collectMetricValues(filteredRows, col.company_id, selectedMetric);
    });
    return map;
  }, [filteredRows, displayColumns, selectedMetric]);

  const virtualStart = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - VIRTUAL_BUFFER);
  const visibleCount = Math.ceil(containerHeight / ROW_HEIGHT) + VIRTUAL_BUFFER * 2;
  const virtualEnd = Math.min(filteredRows.length, virtualStart + visibleCount);
  const virtualRows = filteredRows.slice(virtualStart, virtualEnd);
  const paddingTop = virtualStart * ROW_HEIGHT;
  const paddingBottom = Math.max(0, (filteredRows.length - virtualEnd) * ROW_HEIGHT);

  const handleScroll = (e) => {
    setScrollTop(e.target.scrollTop);
    setContainerHeight(e.target.clientHeight);
  };

  const handleExportCSV = () => {
    const headerCols = displayColumns.map((c) => c.banner_name);
    let csv = `# Metric: ${activeMetric.label} — ${activeMetric.description}\n`;
    csv += `PMG ID,Product's name,${headerCols.join(',')}\n`;
    filteredRows.forEach((row) => {
      const rowData = [
        row.pmg_id,
        '"' + row.name.replace(/"/g, '""') + '"',
        ...displayColumns.map((col) => {
          const val = getMetricDisplayValue(row.cells[col.company_id], selectedMetric);
          return val === '-' ? '' : val;
        }),
      ];
      csv += rowData.join(',') + '\n';
    });
    downloadCsv(csv, 'out_of_date_pmg_company_summary.csv');
  };

  const handleExportAllMetrics = () => {
    const csv = buildAllMetricsCsv(filteredRows, displayColumns);
    downloadCsv(csv, 'out_of_date_all_metrics.csv');
  };

  const footerBg = metricTheme.base;

  return (
    <TableContainer
      ref={containerRef}
      style={{ maxHeight: 'calc(100vh - 240px)', width: '100%', overflow: 'auto' }}
      onScroll={handleScroll}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: '100px' }}>
              PMG ID
            </TableCell>
            <TableCell style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', maxWidth: '400px' }}>
              Product's name
            </TableCell>
            {displayColumns.map((col) => (
              <TableCell
                key={col.company_id}
                align="center"
                style={{
                  backgroundColor: sortColumnId === col.company_id ? metricTheme.base : '#f5f5f5',
                  fontWeight: 'bold',
                  borderBottom: `3px solid ${metricTheme.accent}`,
                  minWidth: 72,
                }}
              >
                <TableSortLabel
                  active={sortColumnId === col.company_id}
                  direction={sortColumnId === col.company_id ? sortDirection : 'asc'}
                  onClick={() => onSortColumn(col.company_id)}
                >
                  {col.banner_name}
                  {pinnedCompanyIds.includes(col.company_id) ? ' *' : ''}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {paddingTop > 0 && (
            <TableRow style={{ height: paddingTop }}>
              <TableCell colSpan={displayColumns.length + 2} style={{ padding: 0, border: 0 }} />
            </TableRow>
          )}
          {virtualRows.map((row) => (
            <OutOfDateTableRow
              key={row.pmg_id}
              row={row}
              columns={displayColumns}
              selectedMetric={selectedMetric}
              columnMaxMap={columnMaxMap}
              heatmapMode={heatmapMode}
              heatmapValueMap={heatmapValueMap}
              onDrilldown={onDrilldown}
              onDetail={onDetail}
              style={{ height: ROW_HEIGHT }}
            />
          ))}
          {paddingBottom > 0 && (
            <TableRow style={{ height: paddingBottom }}>
              <TableCell colSpan={displayColumns.length + 2} style={{ padding: 0, border: 0 }} />
            </TableRow>
          )}
          <TableRow>
            <TableCell style={{ backgroundColor: footerBg, fontWeight: 'bold' }} />
            <TableCell style={{ backgroundColor: footerBg, fontWeight: 'bold' }}>
              {activeMetric.label}
            </TableCell>
            {displayColumns.map((col) => (
              <TableCell
                key={col.company_id}
                align="center"
                style={{ backgroundColor: footerBg, fontWeight: 'bold' }}
              >
                {getMetricFooterValue(filteredRows, col.company_id, selectedMetric)}
              </TableCell>
            ))}
          </TableRow>
          {showDelta && (
            <TableRow>
              <TableCell style={{ backgroundColor: '#fff8e1', fontWeight: 'bold' }} />
              <TableCell style={{ backgroundColor: '#fff8e1', fontWeight: 'bold' }}>
                Δ not updated (vs prev cache)
              </TableCell>
              {displayColumns.map((col) => {
                let sum = 0;
                filteredRows.forEach((row) => {
                  const cell = row.cells[col.company_id];
                  if (cell && cell.not_updated_delta != null) sum += cell.not_updated_delta;
                });
                return (
                  <TableCell key={col.company_id} align="center" style={{ backgroundColor: '#fff8e1', fontWeight: 'bold' }}>
                    {sum >= 0 ? `+${sum}` : sum}
                  </TableCell>
                );
              })}
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={displayColumns.length + 2} align="left">
              <Button variant="outlined" color="primary" startIcon={<SaveAltIcon />} onClick={handleExportCSV} style={{ marginRight: 8 }}>
                Export current metric
              </Button>
              <Button variant="outlined" onClick={handleExportAllMetrics}>
                Export all metrics
              </Button>
              <Typography variant="caption" display="inline" style={{ marginLeft: 12 }}>
                {filteredRows.length} products · Shift+click cell for store page
              </Typography>
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}

function downloadCsv(csv, filename) {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export default function CompanyReportToolOutOfDatePage(props) {
  const token = useSelector((state) => state.session.token);
  const user_type_id = props.match.params.user_type_id;
  const project_id = props.match.params.project_id;
  const [showError, setShowError] = useState(false);
  const [pmgCompanySummary, setPmgCompanySummary] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lastCacheUpdate, setLastCacheUpdate] = useState(null);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedBanners, setSelectedBanners] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [selectedMetric, setSelectedMetric] = useState(() => loadSavedMetric(project_id) || DEFAULT_METRIC);
  const [datePeriod, setDatePeriod] = useState(14);
  const [problemFilter, setProblemFilter] = useState(false);
  const [problemThreshold, setProblemThreshold] = useState(50);
  const [heatmapMode, setHeatmapMode] = useState(false);
  const [showDelta, setShowDelta] = useState(false);
  const [sortColumnId, setSortColumnId] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');
  const [pinnedCompanyIds, setPinnedCompanyIds] = useState([]);
  const [chartsDialogOpen, setChartsDialogOpen] = useState(false);
  const [chartMenuAnchor, setChartMenuAnchor] = useState(null);
  const [viewMenuAnchor, setViewMenuAnchor] = useState(null);
  const [statusByCityDialogOpen, setStatusByCityDialogOpen] = useState(false);
  const [detailDialog, setDetailDialog] = useState(null);

  const activeMetric = findMetricOption(selectedMetric);
  const metricTheme = getMetricTheme(selectedMetric);
  const bannerColumns = useMemo(() => pivotPmgCompanyData(pmgCompanySummary).columns, [pmgCompanySummary]);
  const activeViewCount = [heatmapMode, problemFilter, showDelta].filter(Boolean).length + pinnedCompanyIds.length;

  const cacheCaption = useMemo(() => {
    if (!lastCacheUpdate || lastCacheUpdate.id === 0) return 'No cache data';
    const updatedAt = new Date(lastCacheUpdate.updated_at).toLocaleString();
    return `Cache: ${updatedAt} · ${lastCacheUpdate.duration_seconds}s`;
  }, [lastCacheUpdate]);

  const checkCacheReport = async () => {
    try {
      const response = await axios.post(
        `${envConfig.API_ROOT}${envConfig.API_VERSION}/company-report-tool/data-freshness-reporting/check-report-data`,
        { user_type_id, report_name: 'customer_project_ood' },
        { headers: { Authorization: token } }
      );
      setLastCacheUpdate(response.data.data);
      if (response.data.data && response.data.data.id === 0) {
        setShowError(true);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error checking cache report:', error);
      setShowError(true);
      return false;
    }
  };

  const fetchPmgCompanySummary = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${envConfig.API_ROOT}${envConfig.API_VERSION}/company-report-tool/data-freshness-reporting/out-of-date-pmg-company-summary`,
        {
          user_type_id,
          city: selectedCity,
          banners: selectedBanners,
          date_period: datePeriod,
          search: filterText,
          include_delta: showDelta,
        },
        { headers: { Authorization: token } }
      );
      setPmgCompanySummary(response.data.data || []);
      setTotalRows(response.data.total || (response.data.data || []).length);
    } catch (error) {
      console.error('Error fetching pmg company summary:', error);
      setShowError(true);
    } finally {
      setLoading(false);
    }
  }, [user_type_id, selectedCity, selectedBanners, datePeriod, filterText, showDelta, token]);

  useEffect(() => {
    if (project_id) checkCacheReport();
  }, [project_id]);

  useEffect(() => {
    const t = setTimeout(() => fetchPmgCompanySummary(), 300);
    return () => clearTimeout(t);
  }, [fetchPmgCompanySummary]);

  useEffect(() => {
    saveMetricPreference(project_id, selectedMetric);
  }, [project_id, selectedMetric]);

  const handleSortColumn = (companyId) => {
    if (sortColumnId === companyId) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumnId(companyId);
      setSortDirection('desc');
    }
  };

  const handleDrilldown = (stats) => {
    window.open(
      buildStoreDrilldownUrl(user_type_id, project_id, stats.pmg_id, stats.company_id, selectedMetric, datePeriod),
      '_blank'
    );
  };

  const handleDetail = (stats) => {
    const row = pmgCompanySummary.find((r) => r.pmg_id === stats.pmg_id && r.company_id === stats.company_id);
    setDetailDialog({
      stats,
      pmgId: stats.pmg_id,
      productName: row?.name || '',
      bannerName: stats.banner_name,
    });
  };

  const togglePinBanner = (companyId) => {
    setPinnedCompanyIds((prev) =>
      prev.includes(companyId) ? prev.filter((id) => id !== companyId) : [...prev, companyId]
    );
  };

  return (
    <>
      <AppBar position="sticky" color="inherit">
        <Toolbar>
          <Typography variant="h6" noWrap style={{ marginRight: 20 }}>
            Not updated Report
          </Typography>
          <CitySelector
            selectedCity={selectedCity}
            onCitySelect={setSelectedCity}
            project_id={project_id}
            token={token}
          />
          <BannerSelector
            project_id={project_id}
            token={token}
            selectedBanners={selectedBanners}
            onBannerSelect={(banner) => {
              setSelectedBanners((prev) => {
                if (banner === '') return [];
                if (prev.includes(banner)) return prev.filter((b) => b !== banner);
                return [...prev, banner];
              });
            }}
          />
          <TextField
            variant="outlined"
            size="small"
            placeholder="Filter product"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            style={{ minWidth: 200, marginLeft: 8 }}
          />
          <Box display="flex" style={{ marginLeft: 'auto', gap: 8 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<TuneIcon />}
              onClick={(e) => setViewMenuAnchor(e.currentTarget)}
            >
              View{activeViewCount > 0 ? ` (${activeViewCount})` : ''}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<BarChartIcon />}
              onClick={(e) => setChartMenuAnchor(e.currentTarget)}
            >
              Charts
            </Button>
          </Box>
          <Menu anchorEl={chartMenuAnchor} open={Boolean(chartMenuAnchor)} onClose={() => setChartMenuAnchor(null)}>
            <MenuItem onClick={() => { setChartMenuAnchor(null); setChartsDialogOpen(true); }}>
              Summary Charts
            </MenuItem>
            <MenuItem onClick={() => { setChartMenuAnchor(null); setStatusByCityDialogOpen(true); }}>
              Status by City
            </MenuItem>
            <MenuItem onClick={() => {
              setChartMenuAnchor(null);
              window.open(`/company-report-tool-checked-report/${user_type_id}/${project_id}`, '_blank');
            }}>
              Checked Report
            </MenuItem>
          </Menu>
          <Popover
            open={Boolean(viewMenuAnchor)}
            anchorEl={viewMenuAnchor}
            onClose={() => setViewMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box p={2} minWidth={280}>
              <Typography variant="subtitle2" gutterBottom>Display options</Typography>
              <FormControlLabel
                control={<Switch size="small" checked={heatmapMode} onChange={(e) => setHeatmapMode(e.target.checked)} />}
                label="Heatmap"
              />
              <FormControlLabel
                control={<Switch size="small" checked={problemFilter} onChange={(e) => setProblemFilter(e.target.checked)} />}
                label={`Problems only (≥${problemThreshold}%)`}
              />
              <FormControlLabel
                control={<Switch size="small" checked={showDelta} onChange={(e) => setShowDelta(e.target.checked)} />}
                label="Δ vs previous cache"
              />
              {bannerColumns.length > 0 && (
                <>
                  <Divider style={{ margin: '12px 0' }} />
                  <Typography variant="subtitle2" gutterBottom>Pin banner columns</Typography>
                  <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                    {bannerColumns.map((col) => {
                      const isPinned = pinnedCompanyIds.includes(col.company_id);
                      return (
                        <Chip
                          key={col.company_id}
                          size="small"
                          label={col.banner_name}
                          color={isPinned ? 'primary' : 'default'}
                          variant={isPinned ? 'default' : 'outlined'}
                          onClick={() => togglePinBanner(col.company_id)}
                        />
                      );
                    })}
                  </Box>
                </>
              )}
            </Box>
          </Popover>
        </Toolbar>
        <Box px={2} pb={1}>
          <Typography
            variant="caption"
            display="block"
            style={{ borderLeft: `3px solid ${metricTheme.accent}`, paddingLeft: 8, color: '#666' }}
          >
            <strong>{activeMetric.label}</strong> — {activeMetric.description}
            {' · '}
            <span style={{ color: !lastCacheUpdate || lastCacheUpdate.id === 0 ? '#d32f2f' : undefined }}>
              {cacheCaption}
            </span>
            {' · '}
            {totalRows} PMG×banner rows
          </Typography>
        </Box>
        <Box
          display="flex"
          alignItems="center"
          px={2}
          py={1}
          flexWrap="wrap"
          style={{ backgroundColor: '#f5f5f5', gap: 8, borderTop: '1px solid #e0e0e0' }}
        >
          <TextField
            select
            variant="outlined"
            size="small"
            label="Metric"
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e.target.value)}
            style={{ minWidth: 200 }}
            SelectProps={{ renderValue: (v) => findMetricOption(v).label }}
            InputLabelProps={{ style: { color: metricTheme.accent } }}
          >
            <ListSubheader>Quick presets</ListSubheader>
            {METRIC_PRESETS.map((p) => (
              <MenuItem key={p.id} value={p.metric}>{p.label}</MenuItem>
            ))}
            {METRIC_GROUPS.map((group) => [
              <ListSubheader key={group.group}>{group.groupLabel}</ListSubheader>,
              ...group.options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Box>
                    <Typography variant="body2">{opt.label}</Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      {opt.description}
                    </Typography>
                  </Box>
                </MenuItem>
              )),
            ])}
          </TextField>
          {METRIC_PRESETS.map((p) => (
            <Chip
              key={p.id}
              size="small"
              label={p.label}
              onClick={() => setSelectedMetric(p.metric)}
              style={{
                backgroundColor: selectedMetric === p.metric ? metricTheme.base : undefined,
                borderLeft: selectedMetric === p.metric ? `3px solid ${metricTheme.accent}` : undefined,
              }}
            />
          ))}
          <TextField
            select
            variant="outlined"
            size="small"
            label="Threshold"
            value={datePeriod}
            onChange={(e) => setDatePeriod(Number(e.target.value))}
            style={{ minWidth: 120 }}
          >
            {DATE_PERIOD_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
          {(heatmapMode || problemFilter || showDelta || pinnedCompanyIds.length > 0) && (
            <>
              <Divider orientation="vertical" flexItem style={{ margin: '0 4px' }} />
              <Typography variant="caption" color="textSecondary">Active:</Typography>
              {heatmapMode && (
                <Chip size="small" label="Heatmap" onDelete={() => setHeatmapMode(false)} />
              )}
              {problemFilter && (
                <Chip size="small" label={`Problems ≥${problemThreshold}%`} onDelete={() => setProblemFilter(false)} />
              )}
              {showDelta && (
                <Chip size="small" label="Δ cache" onDelete={() => setShowDelta(false)} />
              )}
              {pinnedCompanyIds.map((id) => {
                const col = bannerColumns.find((c) => c.company_id === id);
                return col ? (
                  <Chip
                    key={id}
                    size="small"
                    label={`Pin: ${col.banner_name}`}
                    onDelete={() => togglePinBanner(id)}
                  />
                ) : null;
              })}
            </>
          )}
        </Box>
      </AppBar>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
          ) : pmgCompanySummary.length > 0 ? (
            <OutOfDateTable
              data={pmgCompanySummary}
              selectedMetric={selectedMetric}
              problemFilter={problemFilter}
              problemThreshold={problemThreshold}
              heatmapMode={heatmapMode}
              sortColumnId={sortColumnId}
              sortDirection={sortDirection}
              onSortColumn={handleSortColumn}
              pinnedCompanyIds={pinnedCompanyIds}
              onDrilldown={handleDrilldown}
              onDetail={handleDetail}
              showDelta={showDelta}
            />
          ) : (
            <Typography style={{ padding: 16 }}>No out of date products found.</Typography>
          )}
        </Grid>
      </Grid>
      <ChartsDialog
        open={chartsDialogOpen}
        onClose={() => setChartsDialogOpen(false)}
        user_type_id={user_type_id}
        selectedCity={selectedCity}
        selectedBanners={selectedBanners}
        token={token}
        selectedMetric={selectedMetric}
        datePeriod={datePeriod}
      />
      <OodCellDetailDialog
        open={Boolean(detailDialog)}
        onClose={() => setDetailDialog(null)}
        stats={detailDialog?.stats}
        pmgId={detailDialog?.pmgId}
        productName={detailDialog?.productName}
        bannerName={detailDialog?.bannerName}
        userTypeId={user_type_id}
        selectedCity={selectedCity}
        selectedBanners={selectedBanners}
        token={token}
        datePeriod={datePeriod}
      />
      <Dialog open={statusByCityDialogOpen} onClose={() => setStatusByCityDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <span>Product Status by City</span>
            <IconButton onClick={() => setStatusByCityDialogOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <ProductStatusByCityChart user_type_id={user_type_id} selectedCity={selectedCity} selectedBanners={selectedBanners} />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        message="Error loading data. Please try again."
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  );
}
